import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import styles from './styles.module.css';
import { connect } from 'react-redux';
import { setBranchId } from '../../../auth/actions';
import { setFiltersVisibility } from '../../../../common/actions';
import { getBillHistory, exportBillHistoryExcel } from '../actions';
import { Dropdown, Input, Button, ExportFormatDialog } from '../../../../common/ui';
import PropTypes from 'prop-types';
import BillHistoryTable from './BillHistoryTable';
import BillHistoryFilterComp from './BillHistoryFilterComp';
import { ORDER_TYPE_LABELS } from '../utils/orderTypeLabels';
import { formatBusinessVerticalLabel } from '../utils/businessVerticalLabels';
import DateRangePicker from '../../../../common/components/DateRangePicker';
import { useSearchParams } from 'react-router-dom';
import { IoMdCloseCircleOutline } from 'react-icons/io';
import { fetchIsRestaurantCompany } from '../utils/companyType';
import { findBusinessDaySettings } from '../../../settings/businessDay/actions';
import { fetchSalesBusinessDays } from '../../sales/actions';
import store from '../../../../store';

const ZONE_OFFSET_MS = (5 * 60 + 30) * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;

const startOfLocalDayMs = (ymd) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || ''));
  if (!match) return null;
  return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])) - ZONE_OFFSET_MS;
};

const overlapsCalendarDate = (day, ymd) => {
  const startOfSpan = startOfLocalDayMs(ymd);
  if (startOfSpan == null) return false;
  const endOfSpan = startOfSpan + DAY_MS;
  const start = Number(day.startDatetime);
  if (!Number.isFinite(start)) return false;
  const end =
    day.endDatetime == null || day.endDatetime === '' ? null : Number(day.endDatetime);
  return start < endOfSpan && (end == null || end >= startOfSpan);
};

const defaultBusinessDayId = (days, ymd) => {
  const match = (days || [])
    .filter((day) => overlapsCalendarDate(day, ymd))
    .sort((a, b) => Number(a.startDatetime) - Number(b.startDatetime))[0];
  return match ? String(match.id) : '';
};

const formatBusinessDayOption = (day) => {
  const shifted = new Date(Number(day.startDatetime) + ZONE_OFFSET_MS);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  let hour = shifted.getUTCHours();
  const minute = String(shifted.getUTCMinutes()).padStart(2, '0');
  const ampm = hour >= 12 ? 'PM' : 'AM';
  hour %= 12;
  if (hour === 0) hour = 12;
  const statusLabel = String(day.status || '').toUpperCase() === 'OPEN' ? 'Open' : 'Sealed';
  return `${shifted.getUTCDate()} ${months[shifted.getUTCMonth()]} ${shifted.getUTCFullYear()}, ${hour}:${minute} ${ampm} ${statusLabel}`;
};

const BillHistory = ({
  setBranchId,
  setFiltersVisibility,
  getBillHistory,
  exportBillHistoryExcel,
  findBusinessDaySettings,
  auth: { branchList, branchId, companyId },
  billHistoryState: {
    isGetBillHistoryLoading,
    billHistoryData,
    totalRecords,
    currentPage,
    totalPages,
    hasNextPage,
    hasPreviousPage,
    getBillHistoryError,
  },
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [filters, setFilters] = useState({});
  const [isRestaurantCompany, setIsRestaurantCompany] = useState(false);
  const [isRestaurantResolved, setIsRestaurantResolved] = useState(false);
  const [businessDayEnabled, setBusinessDayEnabled] = useState(null);
  const [businessDays, setBusinessDays] = useState([]);
  const [businessDaysLoaded, setBusinessDaysLoaded] = useState(false);
  const [settingsError, setSettingsError] = useState('');

  const page = parseInt(searchParams.get('page'), 10) || 1;
  const limit = parseInt(searchParams.get('limit'), 10) || 10;
  const getDefaultDates = () => {
    const today = new Date();
    return {
      fromDate: today.toLocaleDateString('en-CA'),
      toDate: today.toLocaleDateString('en-CA'),
    };
  };
  const defaultDates = getDefaultDates();
  let fromDate = searchParams.get('fromDate') || defaultDates.fromDate;
  let toDate = searchParams.get('toDate') || defaultDates.toDate;
  const billNo = searchParams.get('billNo') || '';
  const periodType = searchParams.get('periodType') || '';
  const startBusinessDayId = searchParams.get('startBusinessDayId') || '';
  const endBusinessDayId = searchParams.get('endBusinessDayId') || '';
  const spanRef = useRef(`${fromDate}|${toDate}`);
  const branchRef = useRef(branchId);

  const businessDayMode = businessDayEnabled === true && periodType === 'businessDay';
  const listReady =
    businessDayEnabled === false ||
    (businessDayEnabled === true && periodType === 'dateRange') ||
    (businessDayMode && Boolean(startBusinessDayId) && Boolean(endBusinessDayId));

  if (!fromDate || !toDate) {
    const todayDate = new Date().toLocaleDateString('en-CA');
    fromDate = fromDate || todayDate;
    toDate = toDate || todayDate;
  }

  const buildFetchParams = useCallback(() => {
    const params = {
      bill_no: billNo,
      start_date: fromDate,
      end_date: toDate,
      page,
      limit,
      restaurantMode: isRestaurantCompany,
    };
    if (filters.cashier?.id != null) {
      params.cashierRegistryId = filters.cashier.id;
    }
    if (filters.user?.id != null) {
      params.cashierId = filters.user.id;
    }
    if (filters.customer?.id != null) {
      params.customerId = filters.customer.id;
    }
    if (filters.orderType) {
      params.orderType = filters.orderType;
    }
    if (filters.businessVertical) {
      params.businessVertical = filters.businessVertical;
    }
    if (businessDayMode && startBusinessDayId && endBusinessDayId) {
      params.periodType = 'businessDay';
      params.startBusinessDayId = startBusinessDayId;
      params.endBusinessDayId = endBusinessDayId;
    }
    return params;
  }, [
    billNo,
    fromDate,
    toDate,
    page,
    limit,
    filters,
    isRestaurantCompany,
    businessDayMode,
    startBusinessDayId,
    endBusinessDayId,
  ]);

  useEffect(() => {
    if (!companyId) {
      setIsRestaurantCompany(false);
      setIsRestaurantResolved(false);
      return;
    }
    let cancelled = false;
    setIsRestaurantResolved(false);
    fetchIsRestaurantCompany(companyId, branchId).then((isRestaurant) => {
      if (!cancelled) {
        setIsRestaurantCompany(isRestaurant);
        setIsRestaurantResolved(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [companyId, branchId]);

  useEffect(() => {
    if (!isRestaurantCompany && filters.orderType) {
      setFilters((prev) => {
        const next = { ...prev };
        delete next.orderType;
        return next;
      });
    }
  }, [isRestaurantCompany, filters.orderType]);

  const exportQueryParams = useMemo(() => {
    const { page: _page, limit: _limit, ...rest } = buildFetchParams();
    return rest;
  }, [buildFetchParams]);

  const branchOptions = (branchList || []).map((item) => ({
    value: item.id,
    label: item.name,
  }));

  const handleChangeBranch = (value) => {
    setBranchId(value);
  };

  const handleBillNoChange = (value) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value && value.trim()) {
        next.set('billNo', value.trim());
      } else {
        next.delete('billNo');
      }
      next.set('page', '1');
      return next;
    });
  };

  const handleDateRangeChange = (dateRange) => {
    if (dateRange?.startDate && dateRange?.endDate) {
      const from = dateRange.startDate.toLocaleDateString('en-CA');
      const to = dateRange.endDate.toLocaleDateString('en-CA');
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('fromDate', from);
        next.set('toDate', to);
        next.set('page', '1');
        return next;
      });
    } else {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('fromDate');
        next.delete('toDate');
        next.set('page', '1');
        return next;
      });
    }
  };

  useEffect(() => {
    const hasPage = searchParams.has('page');
    const hasLimit = searchParams.has('limit');
    const hasFromDate = searchParams.has('fromDate');
    const hasToDate = searchParams.has('toDate');
    if (!hasPage || !hasLimit || !hasFromDate || !hasToDate) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (!hasPage) next.set('page', '1');
        if (!hasLimit) next.set('limit', '10');
        if (!hasFromDate) next.set('fromDate', defaultDates.fromDate);
        if (!hasToDate) next.set('toDate', defaultDates.toDate);
        return next;
      }, { replace: true });
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!branchId) {
      setBusinessDayEnabled(false);
      return undefined;
    }
    setBusinessDayEnabled(null);
    setSettingsError('');
    findBusinessDaySettings(branchId).then((ok) => {
      if (cancelled) return;
      if (!ok) {
        setSettingsError('Failed to load Business Day settings');
        return;
      }
      const rows = store.getState().businessDaySettingsState?.businessDaySettingsData || [];
      const enabled = rows.some(
        (row) =>
          row.param === 'is-business-day-enabled' &&
          String(row.value).toLowerCase() === 'true'
      );
      setBusinessDayEnabled(enabled);
    });
    return () => {
      cancelled = true;
    };
  }, [branchId, findBusinessDaySettings]);

  useEffect(() => {
    if (businessDayEnabled == null) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (!businessDayEnabled) {
        let changed = false;
        ['periodType', 'startBusinessDayId', 'endBusinessDayId'].forEach((key) => {
          if (next.has(key)) {
            next.delete(key);
            changed = true;
          }
        });
        return changed ? next : prev;
      }
      const period = next.get('periodType');
      if (period !== 'businessDay' && period !== 'dateRange') {
        next.set('periodType', 'businessDay');
        next.delete('startBusinessDayId');
        next.delete('endBusinessDayId');
        return next;
      }
      return prev;
    });
  }, [businessDayEnabled, branchId, setSearchParams]);

  useEffect(() => {
    if (branchRef.current === branchId) return;
    branchRef.current = branchId;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('startBusinessDayId');
      next.delete('endBusinessDayId');
      return next;
    });
  }, [branchId, setSearchParams]);

  useEffect(() => {
    const spanKey = `${fromDate}|${toDate}`;
    if (spanRef.current === spanKey) return;
    spanRef.current = spanKey;
    if (periodType !== 'businessDay') return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('startBusinessDayId');
      next.delete('endBusinessDayId');
      return next;
    });
  }, [fromDate, toDate, periodType, setSearchParams]);

  useEffect(() => {
    if (!businessDayMode || !companyId || !branchId) {
      setBusinessDays([]);
      setBusinessDaysLoaded(false);
      return undefined;
    }
    let cancelled = false;
    setBusinessDaysLoaded(false);
    fetchSalesBusinessDays({ fromDate, toDate })
      .then((days) => {
        if (cancelled) return;
        const list = days || [];
        setBusinessDays(list);
        setBusinessDaysLoaded(true);
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          let changed = false;
          const listedIds = new Set(list.map((day) => String(day.id)));
          ['startBusinessDayId', 'endBusinessDayId'].forEach((key) => {
            const current = next.get(key);
            if (current && !listedIds.has(current)) {
              next.delete(key);
              changed = true;
            }
          });
          if (!next.get('startBusinessDayId')) {
            const id = defaultBusinessDayId(list, fromDate);
            if (id) {
              next.set('startBusinessDayId', id);
              changed = true;
            }
          }
          if (!next.get('endBusinessDayId')) {
            const id = defaultBusinessDayId(list, toDate);
            if (id) {
              next.set('endBusinessDayId', id);
              changed = true;
            }
          }
          return changed ? next : prev;
        });
      })
      .catch(() => {
        if (!cancelled) {
          setBusinessDays([]);
          setBusinessDaysLoaded(false);
          setSettingsError('Failed to load business days');
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete('startBusinessDayId');
            next.delete('endBusinessDayId');
            return next;
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [businessDayMode, companyId, branchId, fromDate, toDate, setSearchParams]);

  useEffect(() => {
    if (companyId && branchId && isRestaurantResolved && listReady) {
      getBillHistory(buildFetchParams());
    }
  }, [companyId, branchId, isRestaurantResolved, listReady, buildFetchParams, getBillHistory]);

  const handleFiltersClick = () => {
    setFiltersVisibility(true);
  };

  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('page', '1');
      return next;
    });
  };

  const handleRemoveFilter = (key) => {
    setFilters((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('page', '1');
      return next;
    });
  };

  const handleResetFilters = () => {
    setFilters({});
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('page', '1');
      return next;
    });
  };

  const filterTagLabel = (key, value) => {
    if (key === 'cashier') {
      return `Cashier: ${value.name || value.cashierNo || value.id}`;
    }
    if (key === 'user') return `User: ${value.name || value.id}`;
    if (key === 'customer') return `Customer: ${value.name || value.id}`;
    if (key === 'orderType') {
      const label = ORDER_TYPE_LABELS[value] || value;
      return `Order Type: ${label}`;
    }
    if (key === 'businessVertical') {
      return `Business Vertical: ${formatBusinessVerticalLabel(value) || value}`;
    }
    return `${key}: ${value}`;
  };

  const handleExportClick = () => {
    setExportDialogOpen(true);
  };

  const handleExportConfirm = ({ reportType, format }) => {
    if (businessDayMode && (!startBusinessDayId || !endBusinessDayId)) return;
    exportBillHistoryExcel(exportQueryParams, format, reportType);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerTop}>
          <div className={styles.titleSection}>
            <h1 className={styles.pageTitle}>Bill History</h1>
            <p className={styles.breadcrumb}>Reports / Bill History</p>
          </div>
          {branchId != null && branchOptions.length > 0 && (
            <div className={styles.branchWrap}>
              <Dropdown
                label="Branch"
                options={branchOptions}
                value={branchId}
                onChange={handleChangeBranch}
                placeholder="Select branch"
              />
            </div>
          )}
        </div>
        <div className={styles.addedFiltersDiv}>
          {Object.entries(filters).map(([key, value]) => (
            <span key={key} className={styles.filterTag}>
              {filterTagLabel(key, value)}
              <button
                type="button"
                className={styles.removeFilterBtn}
                onClick={() => handleRemoveFilter(key)}
                aria-label="Remove filter"
              >
                <IoMdCloseCircleOutline />
              </button>
            </span>
          ))}
          {Object.keys(filters).length > 0 && (
            <button
              type="button"
              className={styles.resetFiltersBtn}
              onClick={handleResetFilters}
            >
              Reset Filters
            </button>
          )}
        </div>
      </div>
      <div className={styles.body}>
        <div className={styles.card}>
          <div className={styles.toolbar}>
            <div className={styles.toolbarLeft}>
              <div className={styles.searchWrap}>
                <Input
                  placeholder="Enter bill number"
                  value={billNo}
                  onChange={handleBillNoChange}
                />
              </div>
            </div>
            <div className={styles.toolbarRight}>
              <div className={styles.dateRangeWrap}>
                <DateRangePicker
                  onDateRangeChange={handleDateRangeChange}
                  initialStartDate={new Date(fromDate)}
                  initialEndDate={new Date(toDate)}
                  businessDay={
                    businessDayEnabled
                      ? {
                          periodType: periodType || 'businessDay',
                          onPeriodTypeChange: (value) => {
                            setSearchParams((prev) => {
                              const next = new URLSearchParams(prev);
                              next.set('periodType', value);
                              next.delete('startBusinessDayId');
                              next.delete('endBusinessDayId');
                              next.set('page', '1');
                              return next;
                            });
                          },
                          options: businessDays.map((day) => ({
                            id: String(day.id),
                            label: formatBusinessDayOption(day),
                          })),
                          startId: startBusinessDayId,
                          endId: endBusinessDayId,
                          onStartChange: (id) => {
                            setSearchParams((prev) => {
                              const next = new URLSearchParams(prev);
                              next.set('startBusinessDayId', id);
                              next.set('page', '1');
                              return next;
                            });
                          },
                          onEndChange: (id) => {
                            setSearchParams((prev) => {
                              const next = new URLSearchParams(prev);
                              next.set('endBusinessDayId', id);
                              next.set('page', '1');
                              return next;
                            });
                          },
                          empty: businessDaysLoaded && businessDays.length === 0,
                        }
                      : null
                  }
                />
              </div>
              <Button variant="secondary" onClick={handleFiltersClick}>
                Filters
              </Button>
              <Button variant="secondary" onClick={handleExportClick}>
                Export
              </Button>
              <ExportFormatDialog
                open={exportDialogOpen}
                onClose={() => setExportDialogOpen(false)}
                onConfirm={handleExportConfirm}
                title="Export Bill History"
                subtitle="Select report type and preferred file format for export."
                reportTypes={[
                  {
                    value: 'bill_history',
                    label: 'Bill History',
                    description: 'Export summary-level bill history records.',
                  },
                  {
                    value: 'purchase_item_report',
                    label: 'Purchase Item Report',
                    description: 'Export all items, receipt, and customer details.',
                  },
                ]}
                options={[
                  {
                    value: 'excel',
                    label: 'Excel Spreadsheet (.xlsx)',
                    description: 'Best for data analysis, calculations, and detailed bill tracking.',
                    icon: 'excel',
                    reportTypes: ['bill_history', 'purchase_item_report'],
                  },
                ]}
              />
            </div>
          </div>
          {settingsError && <p className={styles.settingsError}>{settingsError}</p>}
          <BillHistoryFilterComp
            filters={filters}
            setFilters={handleApplyFilters}
            isRestaurantCompany={isRestaurantCompany}
            fromDate={fromDate}
            toDate={toDate}
            periodType={periodType}
            startBusinessDayId={startBusinessDayId}
            endBusinessDayId={endBusinessDayId}
            listReady={listReady}
          />
          <div className={styles.tableWrap}>
            <BillHistoryTable
              data={listReady ? billHistoryData : []}
              isRestaurantCompany={isRestaurantCompany}
              isLoading={isGetBillHistoryLoading}
              error={getBillHistoryError}
              pagination={{
                totalRecords: listReady ? totalRecords : 0,
                currentPage: listReady ? currentPage : 1,
                totalPages: listReady ? totalPages : 0,
                hasNextPage: listReady ? hasNextPage : false,
                hasPreviousPage: listReady ? hasPreviousPage : false,
              }}
              setSearchParams={setSearchParams}
              page={page}
              limit={limit}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

BillHistory.propTypes = {
  setBranchId: PropTypes.func.isRequired,
  setFiltersVisibility: PropTypes.func.isRequired,
  getBillHistory: PropTypes.func.isRequired,
  exportBillHistoryExcel: PropTypes.func.isRequired,
  findBusinessDaySettings: PropTypes.func.isRequired,
  auth: PropTypes.shape({
    branchList: PropTypes.array,
    branchId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  billHistoryState: PropTypes.shape({
    isGetBillHistoryLoading: PropTypes.bool,
    billHistoryData: PropTypes.array,
    totalRecords: PropTypes.number,
    currentPage: PropTypes.number,
    totalPages: PropTypes.number,
    hasNextPage: PropTypes.bool,
    hasPreviousPage: PropTypes.bool,
    getBillHistoryError: PropTypes.string,
  }).isRequired,
};

const mapStateToProps = (state) => ({
  auth: state.authState,
  billHistoryState: state.billHistoryState,
});

export default connect(mapStateToProps, {
  setBranchId,
  setFiltersVisibility,
  getBillHistory,
  exportBillHistoryExcel,
  findBusinessDaySettings,
})(BillHistory);
